import type { JourneyLeg } from './arcticapi/types';
import type { SvgName } from './components/base/Icon/SvgRegistry';

// These are the multimodal transport types the journey planner filter UI intentionally exposes.
export const SUPPORTED_JOURNEY_PLANNER_MODES = [
  'bus',
  'rail',
  'coach',
  'ferry',
  'tram',
  'subway',
] as const;

type StopMarkerMode = 'bus' | 'ferry' | 'rail' | 'tram';

// This is a render-time icon mapping only. It does not rewrite the incoming
// mode on the leg or line object, so unsupported API modes remain visible in
// data and can be handled properly later.
const ICON_BY_MODE: Partial<Record<string, SvgName>> = {
  bus: 'bus',
  coach: 'coach',
  ferry: 'ferry',
  monorail: 'train',
  rail: 'train',
  replacement_bus: 'bus',
  subway: 'tube',
  train: 'train',
  tram: 'tram',
  trolleybus: 'bus',
  tube: 'tube',
};

// Stop markers use a smaller visual family than journey legs. Several related
// transport types intentionally collapse onto the same marker artwork.
const STOP_MARKER_BY_MODE: Partial<Record<string, StopMarkerMode>> = {
  bus: 'bus',
  coach: 'bus',
  ferry: 'ferry',
  metro: 'rail',
  monorail: 'rail',
  rail: 'rail',
  replacement_bus: 'bus',
  subway: 'rail',
  train: 'rail',
  tram: 'tram',
  trolleybus: 'bus',
};

// These modes use operator + headsign instead of line name
export const OPERATOR_HEADSIGN_MODES = new Set([
  'coach',
  'ferry',
  'rail',
  'subway',
]);

// These modes often have weak or placeholder service names in the API, so the
// operator name is usually a better fallback than showing the mode label alone.
const OPERATOR_LABEL_FALLBACK_MODES = new Set([
  'coach',
  'ferry',
  'monorail',
  'rail',
  'subway',
  'tram',
]);

function normalizeDisplayValue(value?: string | null): string | undefined {
  const trimmed = value?.trim();

  if (!trimmed || trimmed.toLowerCase() === 'unknown') {
    return undefined;
  }

  return trimmed;
}

// Normalise API and UI values onto a shared internal form so `rail`,
// `replacement bus`, and `replacement-bus` can all be treated consistently.
export function normalizeTransportMode(
  mode?: string | null,
): string | undefined {
  const trimmed = mode?.trim().toLowerCase();

  if (!trimmed) {
    return undefined;
  }

  return trimmed.replace(/[\s-]+/g, '_');
}

// This is for visible text only. Unknown modes default to the bus label so the
// UI stays usable, but the original mode value is not overwritten elsewhere.
export function formatTransportMode(mode?: string | null): string {
  return (normalizeTransportMode(mode) ?? 'bus').replace(/_/g, ' ');
}

export function titleCaseTransportMode(mode?: string | null): string {
  const label = formatTransportMode(mode);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function getTransportModeIcon(mode?: string | null): SvgName {
  return ICON_BY_MODE[normalizeTransportMode(mode) ?? ''] ?? 'bus';
}

export function getStopMarkerMode(mode?: string | null): StopMarkerMode {
  return STOP_MARKER_BY_MODE[normalizeTransportMode(mode) ?? ''] ?? 'bus';
}

// Prefer a useful human label in summaries and itinerary steps.
// We keep the actual mode for icon/render decisions and only fall back here for text.
export function resolveJourneyServiceLabel(
  leg: Pick<JourneyLeg, 'lineName' | 'mode' | 'operator' | 'line'>,
): string {
  const serviceLabel =
    normalizeDisplayValue(leg.lineName) ??
    normalizeDisplayValue(leg.line?.name);
  const operatorLabel = normalizeDisplayValue(leg.operator?.name);
  const modeLabel = titleCaseTransportMode(leg.mode);

  if (serviceLabel) {
    return serviceLabel;
  }

  if (
    operatorLabel &&
    OPERATOR_LABEL_FALLBACK_MODES.has(normalizeTransportMode(leg.mode) ?? '')
  ) {
    return operatorLabel;
  }

  return operatorLabel ?? modeLabel;
}

/**
 * Get the operator label for modes that use operator + headsign.
 * Returns the operator name, or the mode label if operator is not available.
 */
export function getOperatorLabel(
  leg: Pick<JourneyLeg, 'mode' | 'operator'>,
): string | undefined {
  const operatorLabel = normalizeDisplayValue(leg.operator?.name);
  const modeLabel = titleCaseTransportMode(leg.mode);

  return operatorLabel || modeLabel;
}
